import React, { useState, useEffect } from 'react';
import DatePickerInput from '../../../../components/DatePickerInput';

interface Tarefa {
  id?: number;
  titulo: string;
  prazo?: string;
  data_conclusao?: string;
  membros?: any[];
}

interface TarefasCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  tarefas: Tarefa[];
  onToggleConclusao: (tarefa: Tarefa) => void;
  onAtualizarPrazo: (tarefa: Tarefa, prazo: string | null) => void;
  onAtualizarTitulo: (tarefa: Tarefa, titulo: string) => void;
}

type Visualizacao = 'mes' | 'semana' | 'dia';

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DIAS_SEMANA_LONG = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Paleta do sistema
const COR_PRIMARY  = '#5e72e4';
const AVATAR_CORES = ['#5e72e4', '#2dce89', '#fb6340', '#f5365c', '#11cdef', '#ffd600', '#172b4d'];
const COR_SUCCESS  = '#2dce89';
const COR_DANGER   = '#f5365c';
const COR_TEXT     = '#32325d';
const COR_MUTED    = '#8898aa';
const COR_BORDER   = '#e9ecef';
const COR_BG_LIGHT = '#f8f9fe';

function hoje(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function mesmoDia(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function eAtrasada(t: Tarefa): boolean {
  if (!t.prazo || t.data_conclusao) return false;
  const p = new Date(t.prazo);
  p.setHours(0, 0, 0, 0);
  return p < hoje();
}

function eventCor(t: Tarefa): { bg: string; text: string; pill: string } {
  if (t.data_conclusao) return { bg: '#d4edda', text: '#155724', pill: COR_SUCCESS };
  if (eAtrasada(t))     return { bg: '#fde8ec', text: '#721c24', pill: COR_DANGER };
  return { bg: '#eaecfb', text: '#3d4ec4', pill: COR_PRIMARY };
}

function tarefasNoDia(tarefas: Tarefa[], dia: Date): Tarefa[] {
  return tarefas.filter(t => {
    if (!t.prazo) return false;
    return mesmoDia(new Date(t.prazo), dia);
  });
}

function inicioSemana(ref: Date): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function addDias(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

// ─── Modal principal ─────────────────────────────────────────────────────────

const TarefasCalendarModal: React.FC<TarefasCalendarModalProps> = ({
  isOpen, onClose, tarefas,
  onToggleConclusao, onAtualizarPrazo, onAtualizarTitulo,
}) => {
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('mes');
  const [dataRef, setDataRef] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [tarefaSelecionada, setTarefaSelecionada] = useState<Tarefa | null>(null);
  const [popupPos, setPopupPos] = useState<{ chipLeft: number; chipRight: number; chipTop: number; chipBottom: number } | null>(null);

  useEffect(() => {
    if (!tarefaSelecionada) return;
    const atualizada = tarefas.find(t => t.id === tarefaSelecionada.id);
    if (atualizada) setTarefaSelecionada(atualizada);
  }, [tarefas]);

  if (!isOpen) return null;

  const hj = hoje();

  function navegar(delta: number) {
    setDataRef(prev => {
      const d = new Date(prev);
      if (visualizacao === 'mes') d.setMonth(d.getMonth() + delta);
      else if (visualizacao === 'semana') d.setDate(d.getDate() + delta * 7);
      else d.setDate(d.getDate() + delta);
      return d;
    });
  }

  function tituloHeader(): string {
    if (visualizacao === 'mes') return `${MESES[dataRef.getMonth()]} ${dataRef.getFullYear()}`;
    if (visualizacao === 'semana') {
      const ini = inicioSemana(dataRef);
      const fim = addDias(ini, 6);
      return ini.getMonth() === fim.getMonth()
        ? `${MESES[ini.getMonth()]} ${ini.getFullYear()}`
        : `${MESES_CURTOS[ini.getMonth()]} – ${MESES_CURTOS[fim.getMonth()]} ${fim.getFullYear()}`;
    }
    return `${DIAS_SEMANA_LONG[dataRef.getDay()]}, ${dataRef.getDate()} de ${MESES[dataRef.getMonth()].toLowerCase()} de ${dataRef.getFullYear()}`;
  }

  function handleChipClick(tarefa: Tarefa, e: React.MouseEvent) {
    e.stopPropagation();
    if (tarefaSelecionada?.id === tarefa.id) {
      setTarefaSelecionada(null);
      setPopupPos(null);
      return;
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopupPos({ chipLeft: rect.left, chipRight: rect.right, chipTop: rect.top, chipBottom: rect.bottom });
    setTarefaSelecionada(tarefa);
  }

  function fecharPopup() {
    setTarefaSelecionada(null);
    setPopupPos(null);
  }

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.25)', zIndex: 1099,
      }} />

      <div
        onClick={fecharPopup}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '92vw', maxWidth: 1200, height: '90vh',
          background: '#fff', borderRadius: 8,
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
          zIndex: 1100, display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Barra superior ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderBottom: `1px solid ${COR_BORDER}`,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 600, color: COR_TEXT, marginRight: 4 }}>
            Tarefas
          </span>

          <button
            onClick={e => { e.stopPropagation(); setDataRef(new Date()); }}
            style={{ ...btnOutline }}
          >
            Hoje
          </button>

          <button onClick={e => { e.stopPropagation(); navegar(-1); }} style={btnIcon}>
            <i className="fas fa-chevron-left" style={{ fontSize: '0.65rem' }} />
          </button>
          <button onClick={e => { e.stopPropagation(); navegar(1); }} style={btnIcon}>
            <i className="fas fa-chevron-right" style={{ fontSize: '0.65rem' }} />
          </button>

          <span style={{ fontSize: '1rem', fontWeight: 600, color: COR_TEXT, marginLeft: 4 }}>
            {tituloHeader()}
          </span>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', border: `1px solid ${COR_BORDER}`, borderRadius: 4, overflow: 'hidden' }}>
            {(['mes', 'semana', 'dia'] as Visualizacao[]).map((v, idx) => (
              <button
                key={v}
                onClick={e => { e.stopPropagation(); setVisualizacao(v); }}
                style={{
                  padding: '5px 14px', border: 'none', cursor: 'pointer',
                  fontSize: '0.82rem', fontWeight: 500,
                  borderLeft: idx > 0 ? `1px solid ${COR_BORDER}` : 'none',
                  background: visualizacao === v ? COR_BG_LIGHT : '#fff',
                  color: visualizacao === v ? COR_PRIMARY : COR_TEXT,
                }}
              >
                {v === 'dia' ? 'Dia' : v === 'semana' ? 'Semana' : 'Mês'}
              </button>
            ))}
          </div>

          <button onClick={onClose} style={{ ...btnIcon, color: COR_MUTED }}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* ── Corpo ── */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {visualizacao === 'mes' && (
            <ViewMes dataRef={dataRef} tarefas={tarefas} hj={hj}
              tarefaSelecionada={tarefaSelecionada} onChipClick={handleChipClick} />
          )}
          {visualizacao === 'semana' && (
            <ViewSemana dataRef={dataRef} tarefas={tarefas} hj={hj}
              tarefaSelecionada={tarefaSelecionada} onChipClick={handleChipClick} />
          )}
          {visualizacao === 'dia' && (
            <ViewDia dataRef={dataRef} tarefas={tarefas} hj={hj}
              tarefaSelecionada={tarefaSelecionada} onChipClick={handleChipClick} />
          )}
        </div>
      </div>

      {/* ── Popup de detalhes ── */}
      {tarefaSelecionada && popupPos && (
        <PopupDetalhes
          tarefa={tarefaSelecionada}
          pos={popupPos}
          onClose={fecharPopup}
          onToggleConclusao={() => onToggleConclusao(tarefaSelecionada)}
          onAtualizarPrazo={prazo => onAtualizarPrazo(tarefaSelecionada, prazo)}
          onAtualizarTitulo={titulo => onAtualizarTitulo(tarefaSelecionada, titulo)}
        />
      )}
    </>
  );
};

// ─── Popup de detalhes ────────────────────────────────────────────────────────

interface PopupProps {
  tarefa: Tarefa;
  pos: { chipLeft: number; chipRight: number; chipTop: number; chipBottom: number };
  onClose: () => void;
  onToggleConclusao: () => void;
  onAtualizarPrazo: (prazo: string | null) => void;
  onAtualizarTitulo: (titulo: string) => void;
}

const PopupDetalhes: React.FC<PopupProps> = ({
  tarefa, pos, onClose, onToggleConclusao, onAtualizarPrazo, onAtualizarTitulo,
}) => {
  const [tituloEdit, setTituloEdit] = useState(tarefa.titulo);
  const concluida = !!tarefa.data_conclusao;
  const atrasada  = eAtrasada(tarefa);
  const cor       = eventCor(tarefa);

  useEffect(() => { setTituloEdit(tarefa.titulo); }, [tarefa.titulo, tarefa.id]);

  const POPUP_W = 300;
  const POPUP_H = 380; // altura estimada máxima
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const GAP = 8;

  // Horizontal: preferir à direita do chip, flipar para esquerda se não couber
  let left = pos.chipRight + GAP;
  if (left + POPUP_W > vw - 12) left = pos.chipLeft - POPUP_W - GAP;
  if (left < 12) left = 12;

  // Vertical: preferir alinhado ao topo do chip, flipar para cima se não couber
  let top = pos.chipTop;
  if (top + POPUP_H > vh - 12) top = pos.chipBottom - POPUP_H;
  if (top < 12) top = 12;

  function handleTituloBlur() {
    if (tituloEdit.trim() && tituloEdit.trim() !== tarefa.titulo) {
      onAtualizarTitulo(tituloEdit.trim());
    } else {
      setTituloEdit(tarefa.titulo);
    }
  }

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', left, top, width: POPUP_W,
        background: '#fff', borderRadius: 6,
        boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
        zIndex: 1200,
      }}
    >
      {/* Faixa colorida */}
      <div style={{ height: 5, background: cor.pill, borderRadius: '6px 6px 0 0' }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 8px 0' }}>
        <button onClick={onClose} style={{ ...btnIcon, color: COR_MUTED }}>
          <i className="fas fa-times" style={{ fontSize: '0.8rem' }} />
        </button>
      </div>

      {/* Corpo */}
      <div style={{ padding: '4px 16px 16px' }}>
        {/* Título */}
        <input
          value={tituloEdit}
          onChange={e => setTituloEdit(e.target.value)}
          onBlur={handleTituloBlur}
          onKeyDown={e => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { setTituloEdit(tarefa.titulo); e.currentTarget.blur(); }
          }}
          style={{
            width: '100%', border: 'none', borderBottom: `2px solid ${COR_BORDER}`,
            outline: 'none', fontSize: '0.95rem', fontWeight: 600, color: COR_TEXT,
            padding: '4px 0', marginBottom: 12, background: 'transparent',
            textDecoration: concluida ? 'line-through' : 'none',
            opacity: concluida ? 0.6 : 1,
          }}
        />

        {/* Status */}
        <div style={{ marginBottom: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '2px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
            background: cor.bg, color: cor.text,
          }}>
            <i className={`fas fa-${concluida ? 'check-circle' : atrasada ? 'exclamation-circle' : 'clock'}`} />
            {concluida ? 'Concluída' : atrasada ? 'Atrasada' : 'Pendente'}
          </span>
        </div>

        {/* Prazo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <i className="far fa-calendar-alt" style={{ color: COR_MUTED, fontSize: '0.82rem', flexShrink: 0 }} />
          <DatePickerInput
            value={tarefa.prazo || null}
            onChange={(iso) => onAtualizarPrazo(iso)}
            placeholder="Selecionar prazo..."
            showTime={false}
            style={{ flex: 1 }}
            inputStyle={{
              fontSize: '0.82rem',
              padding: '5px 10px',
              borderRadius: 6,
              border: `1px solid ${COR_BORDER}`,
              color: COR_TEXT,
              background: COR_BG_LIGHT,
            }}
          />
        </div>

        {/* Membros */}
        {tarefa.membros && tarefa.membros.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14 }}>
            <i className="fas fa-users" style={{ color: COR_MUTED, fontSize: '0.85rem', marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '0.72rem', color: COR_MUTED, marginBottom: 6 }}>
                Membros atribuídos
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tarefa.membros.map((m: any, i: number) => {
                  const user = m.user || m;
                  const nome = user?.name || user?.nome || '?';
                  const iniciais = nome.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                  const avatarUrl = user?.avatar_url || user?.avatar;
                  const cor = AVATAR_CORES[user?.id % AVATAR_CORES.length] || COR_PRIMARY;
                  return (
                    <div key={user?.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={nome}
                          style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                          background: cor, color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.6rem', fontWeight: 700,
                        }}>
                          {iniciais}
                        </div>
                      )}
                      <span style={{ fontSize: '0.82rem', color: COR_TEXT }}>{nome}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div style={{ borderTop: `1px solid ${COR_BORDER}`, margin: '0 -16px 12px' }} />

        {/* Toggle conclusão */}
        {concluida ? (
          <button
            onClick={onToggleConclusao}
            style={{
              width: '100%', padding: '8px', border: `1px solid ${COR_BORDER}`,
              borderRadius: 4, background: '#fff', color: COR_MUTED,
              fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <i className="fas fa-undo" style={{ fontSize: '0.75rem' }} />
            Marcar como pendente
          </button>
        ) : (
          <button
            onClick={onToggleConclusao}
            style={{
              width: '100%', padding: '8px', border: 'none',
              borderRadius: 4, background: COR_PRIMARY, color: '#fff',
              fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            <i className="fas fa-check" style={{ fontSize: '0.75rem' }} />
            Marcar como concluída
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Interfaces compartilhadas ────────────────────────────────────────────────

interface ViewProps {
  dataRef: Date;
  tarefas: Tarefa[];
  hj: Date;
  tarefaSelecionada: Tarefa | null;
  onChipClick: (tarefa: Tarefa, e: React.MouseEvent) => void;
}

// ─── View: Mês ───────────────────────────────────────────────────────────────

const ViewMes: React.FC<ViewProps> = ({ dataRef, tarefas, hj, tarefaSelecionada, onChipClick }) => {
  const ano = dataRef.getFullYear();
  const mes = dataRef.getMonth();

  const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
  const diasNoMes         = new Date(ano, mes + 1, 0).getDate();

  const cells: (number | null)[] = [
    ...Array(primeiroDiaSemana).fill(null),
    ...Array.from({ length: diasNoMes }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const semanas: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) semanas.push(cells.slice(i, i + 7));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Cabeçalho */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        borderBottom: `1px solid ${COR_BORDER}`,
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
      }}>
        {DIAS_SEMANA.map((d, i) => (
          <div key={d} style={{
            textAlign: 'center', padding: '8px 0',
            fontSize: '0.72rem', fontWeight: 600,
            color: i === 0 || i === 6 ? COR_DANGER : COR_MUTED,
            textTransform: 'uppercase', letterSpacing: '0.6px',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Grade */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {semanas.map((semana, si) => (
          <div key={si} style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: `1px solid ${COR_BORDER}`,
            flex: 1, minHeight: 90,
          }}>
            {semana.map((dia, di) => {
              const diaDate    = dia !== null ? new Date(ano, mes, dia) : null;
              const eHoje      = diaDate !== null && mesmoDia(diaDate, hj);
              const fimSemana  = di === 0 || di === 6;
              const tarefasDia = diaDate ? tarefasNoDia(tarefas, diaDate) : [];

              return (
                <div key={di} style={{
                  borderRight: di < 6 ? `1px solid ${COR_BORDER}` : 'none',
                  padding: '4px',
                  background: fimSemana && dia !== null ? COR_BG_LIGHT : '#fff',
                  display: 'flex', flexDirection: 'column',
                }}>
                  {dia !== null && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: '50%',
                        background: eHoje ? COR_PRIMARY : 'transparent',
                        color: eHoje ? '#fff' : fimSemana ? COR_DANGER : COR_TEXT,
                        fontSize: '0.78rem', fontWeight: eHoje ? 700 : 400,
                      }}>
                        {dia}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {tarefasDia.slice(0, 3).map((t, ti) => (
                      <EventPill key={t.id || ti} tarefa={t}
                        selecionada={tarefaSelecionada?.id === t.id}
                        onClick={e => onChipClick(t, e)} />
                    ))}
                    {tarefasDia.length > 3 && (
                      <span style={{ fontSize: '0.65rem', color: COR_MUTED, padding: '1px 4px' }}>
                        +{tarefasDia.length - 3} mais
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── View: Semana ─────────────────────────────────────────────────────────────

const ViewSemana: React.FC<ViewProps> = ({ dataRef, tarefas, hj, tarefaSelecionada, onChipClick }) => {
  const ini  = inicioSemana(dataRef);
  const dias = Array.from({ length: 7 }, (_, i) => addDias(ini, i));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        borderBottom: `1px solid ${COR_BORDER}`,
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
      }}>
        {dias.map((dia, i) => {
          const eHoje     = mesmoDia(dia, hj);
          const fimSemana = i === 0 || i === 6;
          return (
            <div key={i} style={{
              textAlign: 'center', padding: '8px 4px',
              borderRight: i < 6 ? `1px solid ${COR_BORDER}` : 'none',
            }}>
              <div style={{
                fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: eHoje ? COR_PRIMARY : fimSemana ? COR_DANGER : COR_MUTED,
              }}>
                {DIAS_SEMANA[dia.getDay()]}
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: '50%',
                background: eHoje ? COR_PRIMARY : 'transparent',
                color: eHoje ? '#fff' : fimSemana ? COR_DANGER : COR_TEXT,
                fontSize: '1.2rem', fontWeight: eHoje ? 700 : 400,
              }}>
                {dia.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1, minHeight: 200 }}>
        {dias.map((dia, i) => {
          const tarefasDia = tarefasNoDia(tarefas, dia);
          const fimSemana  = i === 0 || i === 6;
          return (
            <div key={i} style={{
              borderRight: i < 6 ? `1px solid ${COR_BORDER}` : 'none',
              padding: '6px 4px',
              background: fimSemana ? COR_BG_LIGHT : '#fff',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              {tarefasDia.map((t, ti) => (
                <EventPill key={t.id || ti} tarefa={t} multiLine
                  selecionada={tarefaSelecionada?.id === t.id}
                  onClick={e => onChipClick(t, e)} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── View: Dia ────────────────────────────────────────────────────────────────

const ViewDia: React.FC<ViewProps> = ({ dataRef, tarefas, hj, tarefaSelecionada, onChipClick }) => {
  const tarefasDia = tarefasNoDia(tarefas, dataRef);
  const eHoje      = mesmoDia(dataRef, hj);
  const fimSemana  = dataRef.getDay() === 0 || dataRef.getDay() === 6;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{
        textAlign: 'center', padding: '16px',
        borderBottom: `1px solid ${COR_BORDER}`,
        position: 'sticky', top: 0, background: '#fff', zIndex: 1,
      }}>
        <div style={{
          fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.6px',
          color: eHoje ? COR_PRIMARY : fimSemana ? COR_DANGER : COR_MUTED,
        }}>
          {DIAS_SEMANA[dataRef.getDay()]}
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 52, height: 52, borderRadius: '50%',
          background: eHoje ? COR_PRIMARY : 'transparent',
          color: eHoje ? '#fff' : fimSemana ? COR_DANGER : COR_TEXT,
          fontSize: '1.8rem', fontWeight: 400,
        }}>
          {dataRef.getDate()}
        </div>
      </div>

      <div style={{ flex: 1, padding: '16px 32px' }}>
        {tarefasDia.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 60, color: COR_MUTED }}>
            <i className="far fa-calendar-check" style={{ fontSize: '2.5rem', display: 'block', marginBottom: 10, opacity: 0.4 }} />
            <span style={{ fontSize: '0.9rem' }}>Sem tarefas com prazo neste dia.</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 600, margin: '0 auto' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: COR_MUTED, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>
              Tarefas — {tarefasDia.length}
            </div>
            {tarefasDia.map((t, i) => {
              const cor       = eventCor(t);
              const selecionada = tarefaSelecionada?.id === t.id;
              return (
                <div
                  key={t.id || i}
                  onClick={e => onChipClick(t, e)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 6, cursor: 'pointer',
                    background: selecionada ? cor.bg : '#fff',
                    border: `1px solid ${selecionada ? cor.pill : COR_BORDER}`,
                    borderLeft: `3px solid ${cor.pill}`,
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <span style={{
                    flex: 1, fontSize: '0.88rem', color: COR_TEXT,
                    textDecoration: t.data_conclusao ? 'line-through' : 'none',
                    opacity: t.data_conclusao ? 0.6 : 1,
                  }}>
                    {t.titulo}
                  </span>
                  {t.data_conclusao && (
                    <span style={{ fontSize: '0.68rem', color: '#155724', fontWeight: 600, background: '#d4edda', padding: '1px 7px', borderRadius: 10 }}>
                      Concluída
                    </span>
                  )}
                  {eAtrasada(t) && (
                    <span style={{ fontSize: '0.68rem', color: '#721c24', fontWeight: 600, background: '#fde8ec', padding: '1px 7px', borderRadius: 10 }}>
                      Atrasada
                    </span>
                  )}
                  <i className="fas fa-chevron-right" style={{ color: COR_BORDER, fontSize: '0.65rem' }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── EventPill ────────────────────────────────────────────────────────────────

interface EventPillProps {
  tarefa: Tarefa;
  selecionada: boolean;
  onClick: (e: React.MouseEvent) => void;
  multiLine?: boolean;
}

const EventPill: React.FC<EventPillProps> = ({ tarefa, selecionada, onClick, multiLine }) => {
  const cor = eventCor(tarefa);
  return (
    <div
      onClick={onClick}
      title={tarefa.titulo}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 4,
        padding: '2px 5px', borderRadius: 3, cursor: 'pointer',
        background: selecionada ? cor.bg : cor.bg + 'aa',
        borderLeft: `3px solid ${cor.pill}`,
        overflow: 'hidden',
      }}
    >
      <span style={{
        fontSize: '0.72rem', fontWeight: 500, color: cor.text, lineHeight: 1.4,
        overflow: 'hidden',
        textOverflow: multiLine ? undefined : 'ellipsis',
        whiteSpace: multiLine ? 'normal' : 'nowrap',
        wordBreak: multiLine ? 'break-word' : undefined,
        textDecoration: tarefa.data_conclusao ? 'line-through' : 'none',
        opacity: tarefa.data_conclusao ? 0.7 : 1,
      }}>
        {tarefa.titulo}
      </span>
    </div>
  );
};

// ─── Estilos base ─────────────────────────────────────────────────────────────

const btnIcon: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: COR_TEXT, fontSize: '0.85rem',
  padding: 0, width: 30, height: 30, borderRadius: '50%',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const btnOutline: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 4, border: `1px solid ${COR_BORDER}`,
  background: '#fff', color: COR_TEXT, fontSize: '0.82rem',
  fontWeight: 500, cursor: 'pointer',
};

export default TarefasCalendarModal;
